import { createSelector } from '@ngrx/store';
import { createSlice, PayloadAction } from 'ngrx-slice';

import {
  PageableObject,
  ProductCollectorResponse,
  ProductSearchResponse,
  ProductSheetResponse,
  ProductShopsPageResponse,
  ProductVariantResponse,
  SearchTextExtension,
  SortObject,
} from '@app/api/marketplace/models';
import { OrderListSelectors } from '@app/order-list/store/order-list.slice';
import { SearchRequest } from '@search/interfaces/search-request.intarface';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { PageResponse } from '@shared/interfaces/page-response.interface';

import { ShopRequest } from '../interfaces/shop-request.interface';

export interface State {
  searchValue: SearchRequest;
  shopsLoading: boolean;
  shops: PageResponse<ProductShopsPageResponse>;
  activeProduct: ProductSheetResponse | null;
  activeProductLoading: boolean;
  productVariants: Array<ProductVariantResponse>;
  productVariantsLoading: boolean;
  collectorVariants: Array<ProductCollectorResponse>;
  collectorVariantsLoading: boolean;
  shopRequest: ShopRequest;
  brands: Array<string>;
  searchedItemsLoadError: boolean;
  searchedItemsLoading: boolean;
  search: PageResponse<ProductSearchResponse>;
  isSidenavOpen: boolean;
  productSuggestions: Array<SearchTextExtension>;
  searchedWords: { [key: string]: boolean };
  suggestionByText: string;
}

export const initialSearchState = {
  totalPages: 0,
  totalElements: 0,
  size: 0,
  content: [],
  number: 0,
  sort: {} as SortObject,
  pageable: {} as PageableObject,
  first: true,
  last: true,
  numberOfElements: 0,
  empty: true,
};

export const initialShopsState = {
  totalPages: 0,
  totalElements: 0,
  size: 0,
  content: [],
  number: 0,
  sort: {} as SortObject,
  pageable: {} as PageableObject,
  first: true,
  last: true,
  numberOfElements: 0,
  empty: true,
};

export const initialState: State = {
  searchValue: {
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.searchPageSizeOptions[0],
  },
  shopRequest: {
    packagingUnitId: '',
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.searchPageSizeOptions[0],
  },
  activeProduct: null,
  activeProductLoading: false,
  productVariants: [],
  productVariantsLoading: false,
  collectorVariants: [],
  collectorVariantsLoading: false,
  shopsLoading: false,
  shops: initialShopsState,
  brands: [],
  searchedItemsLoadError: false,
  searchedItemsLoading: false,
  search: initialSearchState,
  isSidenavOpen: true,
  productSuggestions: [],
  searchedWords: {},
  suggestionByText: '',
};

export const {
  actions: ProductSearchActions,
  selectors,
  ...ProductSearchFeature
} = createSlice({
  name: 'productSearch',
  initialState,
  reducers: {
    searchProducts: {
      trigger: (state) => {
        state.searchedItemsLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: PageResponse<ProductSearchResponse> }>
      ) => {
        state.search = action.response;
        state.searchedItemsLoading = false;
        state.searchedItemsLoadError = false;
      },
      failure: (state) => ({
        ...state,
        searchedItemsLoading: false,
        searchedItemsLoadError: true,
      }),
    },
    searchShops: {
      trigger: (state, action: PayloadAction<{ request: ShopRequest }>) => {
        state.shops = initialShopsState;
        state.shopsLoading = true;
        state.shopRequest = action.request;
      },
      success: (
        state,
        action: PayloadAction<{
          response: PageResponse<ProductShopsPageResponse>;
        }>
      ) => {
        state.shops = action.response;
        state.shopsLoading = false;
      },
      failure: (state) => ({
        ...state,
        shopsLoading: false,
      }),
    },
    getBrands: {
      trigger: (state) => {
        state.brands = [];
      },
      success: (state, action: PayloadAction<{ response: Array<string> }>) => {
        state.brands = action.response;
      },
    },
    getActiveProduct: {
      trigger: (
        state,
        _action: PayloadAction<{
          packagingUnitId: string;
        }>
      ) => {
        state.activeProduct = null;
        state.activeProductLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{
          response: ProductSheetResponse;
        }>
      ) => {
        state.activeProduct = action.response;
        state.activeProductLoading = false;
      },
    },
    getProductVariants: {
      trigger: (state, action: PayloadAction<{ packagingUnitId: string }>) => {
        state.productVariants = [];
        state.productVariantsLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: Array<ProductVariantResponse> }>
      ) => {
        state.productVariants = action.response;
        state.productVariantsLoading = false;
      },
    },
    getCollectorVariants: {
      trigger: (state, action: PayloadAction<{ packagingUnitId: string }>) => {
        state.collectorVariants = [];
        state.collectorVariantsLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: Array<ProductCollectorResponse> }>
      ) => {
        state.collectorVariants = action.response;
        state.collectorVariantsLoading = false;
      },
    },
    updateSearchValue: {
      trigger: (state, action: PayloadAction<{ filter: SearchRequest }>) => {
        state.searchValue = {
          ...state.searchValue,
          ...action.filter,
        };
        if (action.filter?.searchText) {
          state.searchedWords[action.filter.searchText] = true;
        }
        if (
          action.filter?.searchText &&
          state.productSuggestions.some(
            (suggestion) =>
              suggestion.extendedText === action.filter?.searchText
          )
        ) {
          state.searchedWords[action.filter.searchText] = true;
        }
        if (
          !action.filter?.hasOwnProperty('page') &&
          !action.filter?.hasOwnProperty('sort')
        ) {
          state.searchValue = {
            ...state.searchValue,
            page: 0,
          };
        }
      },
      success: () => {},
    },
    updateIsSidenavOpen: {
      trigger: (state, action: PayloadAction<{ isSidenavOpen: boolean }>) => ({
        ...state,
        isSidenavOpen: action.isSidenavOpen,
      }),
      success: () => {},
    },
    getProductSuggestions: {
      trigger: (state, action: PayloadAction<{ searchText: string }>) => ({
        ...state,
        suggestionByText: action.searchText,
      }),
      success: (
        state,
        action: PayloadAction<{ response: Array<SearchTextExtension> }>
      ) => {
        state.productSuggestions = action.response;
      },
    },
    getSearchedWords: {
      trigger: (state) => ({
        ...state,
      }),
      success: (
        state,
        action: PayloadAction<{
          response: { [key: string]: boolean };
        }>
      ) => {
        state.searchedWords = action.response;
      },
    },
    reset: (_) => initialState,
  },
});

export const ProductSearchSelectors = {
  ...selectors,
  selectSearchedItems: createSelector(
    selectors.selectSearch,
    OrderListSelectors.selectOrderList,
    (search, orderList) =>
      search.content.map((item) => {
        const orderListItem = orderList.content.find(
          (orderItem) => orderItem.packagingUnitId === item.packagingUnitId
        );
        return {
          ...item,
          amount: orderListItem?.quantity,
          orderListId: orderListItem?.id,
        };
      })
  ),
  selectShops: createSelector(selectors.selectShops, (shops) => shops.content),
  selectTotalSearchedItems: createSelector(
    selectors.selectSearch,
    (search) => search.totalElements
  ),
  selectTotalShops: createSelector(
    selectors.selectShops,
    (shops) => shops.totalElements
  ),
  selectSearchTextLength: createSelector(
    selectors.selectSearchValue,
    (searchValue) => searchValue.searchText?.length
  ),
  selectProductSuggestionsList: createSelector(
    selectors.selectProductSuggestions,
    selectors.selectSearchedWords,
    selectors.selectSuggestionByText,
    (
      suggestions,
      searchedWords,
      suggestionByText
    ): Array<{
      value: string;
      searched?: boolean;
      firstMatchingIndex?: number;
    }> =>
      suggestionByText.length > 0
        ? [
            ...Object.keys(searchedWords)
              .filter((searchedWord) =>
                suggestions.some(
                  (suggestion) => searchedWord === suggestion.extendedText!
                )
              )
              .map((searchedWord) => ({
                value: searchedWord,
                searched: true,
                firstMatchingIndex: suggestions.find(
                  (suggestion) => suggestion.extendedText === searchedWord
                )?.firstMatchingIndex,
              }))
              .slice(-3)
              .reverse(),
            ...suggestions
              .filter((suggestion) =>
                Object.keys(searchedWords).every(
                  (searchedWord) => searchedWord !== suggestion.extendedText!
                )
              )
              .map((suggestion) => ({
                value: suggestion.extendedText!,
                searched: false,
                firstMatchingIndex: suggestion.firstMatchingIndex,
              }))
              .slice(0, 3),
          ]
        : [
            ...Object.keys(searchedWords)
              .map((searchedWord) => ({
                value: searchedWord,
                searched: true,
                firstMatchingIndex: suggestions.find(
                  (suggestion) => suggestion.extendedText === searchedWord
                )?.firstMatchingIndex,
              }))
              .slice(-3)
              .reverse(),
          ]
  ),
};
